<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <v-checkbox v-model="fields.cardTitle.show" class="mb-2 mt-0" label="Show title block" hide-details />
          <label class="label-input mb-2 d-block">Title content</label>
          <ckeditor :editor="editor" v-model="fields.cardTitle.content" :config="editorConfig" />
        </div>

        <div class="mb-4">
          <label class="label-input">Main image (center)</label>
          <div class="group-item">
            <UploadImage
              @callback="handleUploadMainImage"
              :image="{ imageSrc: fields.mainImage.src || '' }"
              :height="`200`"
              :typeUpload="'image'"
              @callbackDelete="() => (fields.mainImage.src = '')"
            />
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Satellite items ({{ fields.listItemsSatellite.length }})</label>
            <v-btn small text color="primary" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add item</span>
            </v-btn>
          </div>
          <p class="hint-text">
            Items are split evenly between the left and right sides of the image, in order — the first half goes left,
            the rest goes right.
          </p>
          <v-expansion-panels accordion class="mt-2">
            <v-expansion-panel
              v-for="(item, index) in fields.listItemsSatellite"
              :key="`feetscare-satellite-item-${index}`"
            >
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <span>
                    Item {{ index + 1 }}
                    <em class="side-hint">({{ isLeftItem(index) ? 'left' : 'right' }})</em>
                  </span>
                  <v-btn icon @click.stop="handleRemoveItem(index)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="d-flex align-center gap-3">
                      <input
                        type="color"
                        style="width: 40px; height: 40px; margin-right: 12px"
                        :value="item.bgItem"
                        @change="e => (item.bgItem = e.target.value)"
                      />
                      <v-text-field v-model="item.bgItem" label="Background" hide-details />
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="d-flex align-center gap-3">
                      <input
                        type="color"
                        style="width: 40px; height: 40px; margin-right: 12px"
                        :value="item.colorItem"
                        @change="e => (item.colorItem = e.target.value)"
                      />
                      <v-text-field v-model="item.colorItem" label="Text color" hide-details />
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="item.widthImg"
                      type="number"
                      suffix="px"
                      label="Icon width"
                      hide-details
                    />
                  </v-col>
                </v-row>

                <div class="mt-3">
                  <label class="label-input mb-2 d-block">Icon</label>
                  <v-radio-group v-model="item.useSvg" row hide-details class="mt-0">
                    <v-radio label="Icon Image" :value="false"></v-radio>
                    <v-radio label="Icon SVG" :value="true"></v-radio>
                  </v-radio-group>
                  <div class="mt-2">
                    <UploadImage
                      v-if="!item.useSvg"
                      @callback="obj => handleUploadIconImage(obj, index)"
                      :image="{ imageSrc: item.icon || '' }"
                      :height="`100`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveIconImage(index)"
                    />
                    <UploadSVG
                      v-else
                      :svg="{ svgContent: item.iconSvg }"
                      :height="`100`"
                      :heightPreview="`300`"
                      @callback="obj => handleUploadIconSvg(obj, index)"
                      @callbackDelete="() => handleRemoveIconSvg(index)"
                    />
                  </div>
                </div>

                <label class="label-input mb-2 mt-3 d-block">Title</label>
                <ckeditor :editor="editor" v-model="item.title" :config="editorConfig" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import UploadSVG from '@/components/form/uploadFile/UploadSVG.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import { updateHtmlFromData } from '../common/html/update.js';

export default {
  name: 'feetscare-satellite-section-editor',
  components: {
    UploadImage,
    UploadSVG,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Feetscare Satellite',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        cardTitle: {
          show: true,
          content:
            '<h2 style="text-align: center"><span style="color: rgb(222, 165, 139)">Transform Stress into</span> <span style="color: rgb(0, 0, 0)">Blissful Comfort Daily</span></h2>',
        },
        mainImage: {
          src: '',
        },
        listItemsSatellite: [],
      },
    };
  },
  computed: {
    halfIndex() {
      return Math.ceil(this.fields.listItemsSatellite.length / 2);
    },
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    isLeftItem(index) {
      return index < this.halfIndex;
    },
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        cardTitle: { ...this.fields.cardTitle, ...(extract.cardTitle || {}) },
        mainImage: { ...this.fields.mainImage, ...(extract.mainImage || {}) },
        listItemsSatellite: Array.isArray(extract.listItemsSatellite)
          ? extract.listItemsSatellite
          : this.fields.listItemsSatellite,
      };
    },
    handleUploadMainImage(obj) {
      this.fields.mainImage.src = obj.imageSrc || '';
    },
    handleAddItem() {
      this.fields.listItemsSatellite.push({
        bgItem: '#dc9376',
        colorItem: '#ffffff',
        widthImg: 34,
        useSvg: false,
        icon: '',
        iconSvg: '',
        title: '<p style="text-align: center">New item</p>',
      });
    },
    handleRemoveItem(index) {
      this.fields.listItemsSatellite.splice(index, 1);
    },
    handleUploadIconImage(obj, index) {
      if (!this.fields.listItemsSatellite[index]) return;
      this.$set(this.fields.listItemsSatellite[index], 'icon', obj.imageSrc || '');
    },
    handleRemoveIconImage(index) {
      if (!this.fields.listItemsSatellite[index]) return;
      this.$set(this.fields.listItemsSatellite[index], 'icon', '');
    },
    handleUploadIconSvg(obj, index) {
      if (!this.fields.listItemsSatellite[index]) return;
      this.$set(this.fields.listItemsSatellite[index], 'iconSvg', obj.svgContent || '');
    },
    handleRemoveIconSvg(index) {
      if (!this.fields.listItemsSatellite[index]) return;
      this.$set(this.fields.listItemsSatellite[index], 'iconSvg', '');
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionFeetscareSatellite(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.hint-text {
  font-size: 13px;
  color: #888;
  margin: 4px 0 0;
}
.side-hint {
  font-size: 12px;
  color: #888;
  font-style: normal;
  margin-left: 4px;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
